import type { Property } from '@/types/property'
import type { City, Category } from '@/types/home'
import type { ApiCity, ApiCategory, ApiMagazine } from '@/types/api'
import type { MagazineIssue } from '@/data/mock/magazine'
import { mockHomeData } from '@/data/mock/home'
import { API_BASE_URL } from '@/lib/constants'
import { normalizeMediaUrl } from '@/lib/normalizeMediaUrl'
import { cityFallbackImage } from '@/lib/cityFallbacks'

/** Neutral book-cover placeholder when a magazine has no uploaded cover. */
export const PLACEHOLDER_MAGAZINE_COVER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="800"><rect width="100%" height="100%" fill="#0f5d9a"/><text x="50%" y="50%" font-family="sans-serif" font-size="48" fill="#ffffff" text-anchor="middle" dominant-baseline="middle">QBC E-Book</text></svg>',
  )

/** Map an API magazine into the reader's MagazineIssue shape (PDF-backed). */
export function mapApiMagazine(m: ApiMagazine): MagazineIssue {
  return {
    id: m.id,
    slug: m.slug,
    title: m.title,
    edition: m.edition ?? '',
    subtitle: m.subtitle ?? '',
    excerpt: m.excerpt ?? m.subtitle ?? `Read ${m.title} on the QBC digital reader.`,
    sections: [],
    coverImage: normalizeMediaUrl(m.coverImage) || PLACEHOLDER_MAGAZINE_COVER,
    publishedAt: m.publishedAt ?? m.publishMonth ?? '',
    highlights: [],
    author: 'QBC Editorial Team',
    readTime: 0,
    tags: [],
    city: m.city ?? undefined,
    // Same-origin via the dev proxy; CORS-allowed (api/*) in production.
    pdfUrl: `${API_BASE_URL}/magazines/${m.id}/file`,
  }
}

/** Neutral fallback when a listing has no usable image. */
export const PLACEHOLDER_PROPERTY_IMAGE =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="640" height="480"><rect width="100%" height="100%" fill="#e5e7eb"/><text x="50%" y="50%" font-family="sans-serif" font-size="24" fill="#9ca3af" text-anchor="middle" dominant-baseline="middle">QBC</text></svg>',
  )

function flag(value: unknown): boolean {
  return value === true || value === 1 || value === '1'
}

/** Guarantee a renderable Property (image is always a string). */
export function normalizeProperty(p: Property): Property {
  const image = normalizeMediaUrl(p.image) || PLACEHOLDER_PROPERTY_IMAGE
  const images = (p.images?.length ? p.images : p.image ? [p.image] : [])
    .map((src) => normalizeMediaUrl(src) || PLACEHOLDER_PROPERTY_IMAGE)

  return {
    ...p,
    image,
    images: images.length ? images : [image],
    isHot: flag(p.isHot),
  }
}

export function mapApiCity(city: ApiCity, index = 0): City {
  const uploaded = normalizeMediaUrl(city.image)

  return {
    id: city.id,
    name: city.name,
    slug: city.slug,
    image: uploaded || cityFallbackImage(city.slug, index),
    propertyCount: city.propertyCount,
  }
}

export function mapApiCategory(category: ApiCategory, index: number): Category {
  const fallback = mockHomeData.categories[index % mockHomeData.categories.length]
  return {
    id: category.id,
    name: category.name,
    slug: category.slug,
    icon: category.icon || fallback?.icon || 'building',
    count: category.count,
    displayName: category.name,
    image: normalizeMediaUrl(category.image) || fallback?.image,
  }
}
